import isUrl from 'is-url';
import { last, pipe, split, trim } from 'ramda';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Heading, HelpLink, Input, Text } from '../../../../components/common';
import { LinkIcon } from '../../../../components/common/SourceIcon/Icons/LinkIcon';
import { HELP_LINKS } from '../../../../constants/HelpLinks';
import { ExternalSource } from '../../../../constants/Source';
import { generateRandomGuid4 } from '../../../../critical/utils';
import { createFilesBiOptionsMap } from '../../../../utils/bi';
import { useTrackAddMediaImportClickAnalytics } from '../../../../utils/hooks/bi/useTrackAddMediaImportClickAnalytics';
import { useTrackAddMediaUrlParsing } from '../../../../utils/hooks/bi/useTrackAddMediaUrlParsing';
import { useEffectButSkipMount } from '../../../../utils/hooks/useEffectButSkipMount';
import { MediaManagerSDKFile } from '../../../../utils/media-manager-sdk';
import { getFileExtensionFromURL } from '../../../../utils/media-manager-sdk/items';
import { ConnectSourceIcon } from '../ConnectSourceIcon/ConnectSourceIcon';
import styles from './UrlImporter.scss';
import { IOuter } from './UrlImporter.types';

export const UrlImporter: FC<IOuter> = ({ openedFrom, onSelect }) => {
  const { formatMessage } = useIntl();

  const [url, setUrl] = useState('');
  const handleUrlChange = useCallback((event) => setUrl(trim(event.target.value)), []);

  const isEmpty = url.length === 0;
  const isUrlValid = isUrl(url);
  const isShowError = !isEmpty && !isUrlValid;

  const { trackAddMediaImportClick } = useTrackAddMediaImportClickAnalytics();
  const { trackAddMediaUrlParsingCompleted } = useTrackAddMediaUrlParsing();

  useEffectButSkipMount(() => {
    if (!url) {
      return;
    }

    trackAddMediaUrlParsingCompleted({
      success: isUrlValid,
      siteUrl: url,
    });
  }, [url, isUrlValid, trackAddMediaUrlParsingCompleted]);

  const handleImportButtonClick = useCallback(() => {
    const correlationId = generateRandomGuid4();
    const batchId = generateRandomGuid4();
    const fileFormat = getFileExtensionFromURL(url);
    const file = {
      name: pipe(split('/'), last, decodeURIComponent)(url),
      kind: 'file',
      fileUrl: url,
      previewUrl: url,
      bi: {
        correlationId,
        batchId,
        fileFormat,
      },
    };

    trackAddMediaImportClick({
      correlationId,
      batchId,
      batch: false,
      batchSize: 1,
      fileFormat,
      target: ExternalSource.Link,
    });

    onSelect([file as MediaManagerSDKFile], {
      bi: {
        files: createFilesBiOptionsMap([file as MediaManagerSDKFile], openedFrom, ExternalSource.Link),
      },
    });
  }, [openedFrom, onSelect, url, trackAddMediaImportClick]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.inner}>
          <ConnectSourceIcon className={styles.sourceIconWrapper}>
            <LinkIcon isSecondaryColor />
          </ConnectSourceIcon>

          <Heading size="medium" className={styles.pasteFileLabel}>
            <FormattedMessage id="files-importer.import-file-from-url" />
          </Heading>

          <Input
            border="round"
            placeholder={formatMessage({ id: 'files-importer.paste-link-here' })}
            status={isShowError ? 'error' : undefined}
            statusMessage={
              isShowError && (
                <div className={styles.errorMessageWrapper}>
                  <FormattedMessage id="files-importer.enter-valid-url" />
                </div>
              )
            }
            suffix={
              isUrlValid && (
                <div className={styles.importButtonWrapper}>
                  <Button size="tiny" onClick={handleImportButtonClick} dataHook="url-importer-import-button">
                    <FormattedMessage id="files-importer.import-by-url-button" />
                  </Button>
                </div>
              )
            }
            onChange={handleUrlChange}
            dataHook="url-importer-input-field"
          />
        </div>
      </div>

      <Text className={styles.footer} weight="thin" size="small" secondary>
        <FormattedMessage
          id="files-importer.import-by-url-supported-file-types"
          values={{
            readMoreLink: (
              <HelpLink article={HELP_LINKS.LEARN_MORE_SUPPORTED_TYPES_AND_SIZES} size="small">
                <FormattedMessage id="files-importer.read-more" />
              </HelpLink>
            ),
          }}
        />
      </Text>
    </div>
  );
};
